/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   MaskedInput component, based off of the one in web-pro
 */

import React, { useCallback, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { TextInput, TextInputProps } from '../TextInput'
import { FormatChars, selectMaskedValue } from './helpers/selectMaskedValue'

// NOTE: format chars should always be single chars, although TS does not enforce
// ts-toolbelt's Omit differs from the built-in Omit by being "distributive over unions"
// https://davidgomes.com/pick-omit-over-union-types-in-typescript/
export interface MaskedInputProps extends Omit<TextInputProps, 'onChange'> {
  disabled?: boolean
  formatChars?: FormatChars
  mask: string
  onChange?: (newValue: string) => void
  value?: string
}

export const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  (
    { formatChars, mask, onChange: onChangeProp, ...props }: MaskedInputProps,
    ref
  ) => {
    const isControlled = 'value' in props
    const { value, ...forwardProps } = props
    const [element, setElement] = useState<HTMLInputElement | null>(null)
    const [maskedValue, setMaskedValue] = useState(
      selectMaskedValue(value, mask, formatChars).newValue
    )
    const onChange = useCallback(
      (
        event:
          | Parameters<NonNullable<TextInputProps['onChange']>>[0]
          | {
              currentTarget?: undefined
              target: HTMLInputElement
            }
      ) => {
        const target = event.currentTarget ?? event.target
        const { newValue, selectionStart } = selectMaskedValue(
          target.value,
          mask,
          formatChars,
          'selectionStart' in target ? target.selectionStart : null
        )
        flushSync(() => {
          if (isControlled) {
            setMaskedValue(newValue)
          } else {
            target.value = newValue
          }
          if (onChangeProp) {
            onChangeProp(newValue)
          }
        })
        if (
          typeof selectionStart === 'number' &&
          target instanceof HTMLInputElement
        ) {
          target.selectionStart = target.selectionEnd = selectionStart
        }
      },
      [formatChars, isControlled, mask, onChangeProp]
    )
    useEffect(() => {
      if (isControlled || value !== undefined) {
        setMaskedValue(selectMaskedValue(value, mask, formatChars).newValue)
      }
    }, [formatChars, isControlled, mask, value])
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(element)
      } else if (ref) {
        ref.current = element
      }
    }, [element, ref])
    useEffect(() => {
      const formatRegexes =
        formatChars &&
        Object.values(formatChars).map((pattern) => new RegExp(pattern))
      if (element && formatRegexes?.length) {
        const onBeforeInput = ({ inputType, currentTarget }: InputEvent) => {
          if (currentTarget instanceof HTMLInputElement) {
            const { selectionEnd, selectionStart, value } = currentTarget
            if (selectionStart === selectionEnd && selectionStart != null) {
              let indexToDeleteStart: number | undefined
              let indexToDeleteEnd: number | undefined
              let directionToDelete: 1 | -1 | undefined
              if (inputType === 'deleteContentBackward') {
                indexToDeleteStart = selectionStart - 1
                directionToDelete = -1
              } else if (inputType === 'deleteContentForward') {
                indexToDeleteStart = selectionStart
                directionToDelete = 1
              }
              if (directionToDelete && indexToDeleteStart !== undefined) {
                indexToDeleteEnd = indexToDeleteStart + 1
                while (
                  !formatRegexes.some(
                    (pattern) =>
                      indexToDeleteStart !== undefined &&
                      pattern.test(
                        value.substring(indexToDeleteStart, indexToDeleteEnd)
                      )
                  )
                ) {
                  if (directionToDelete === 1) {
                    if (indexToDeleteEnd + 1 > value.length) {
                      break
                    }
                    indexToDeleteEnd += 1
                  } else {
                    if (indexToDeleteStart - 1 < 0) {
                      break
                    }
                    indexToDeleteStart -= 1
                  }
                }
                if (indexToDeleteEnd - indexToDeleteStart !== 1) {
                  currentTarget.value =
                    value.substring(0, indexToDeleteStart) +
                    value.substring(indexToDeleteEnd)
                  currentTarget.setSelectionRange(
                    indexToDeleteStart,
                    indexToDeleteStart
                  )
                  onChange({ target: currentTarget })
                }
              }
            }
          }
        }
        element.addEventListener('beforeinput', onBeforeInput)
        return () => {
          element.removeEventListener('beforeinput', onBeforeInput)
        }
      }
    }, [element, formatChars, onChange])

    return (
      <TextInput
        {...forwardProps}
        onChange={onChange}
        ref={setElement}
        {...(isControlled ? { value: maskedValue } : undefined)}
      />
    )
  }
)
